import React, { useMemo } from "react";
import { Calendar, Clock } from "../AdminIcons";

const STATUS_STYLE_MAP = {
  scheduled: "border border-[#FFCA28] bg-[#FFCA2833] text-[#FFCA28]",
  upcoming: "border border-[#FFCA28] bg-[#FFCA2833] text-[#FFCA28]",
  "in-progress": "border border-[#3B82F6] bg-[#3B82F633] text-[#3B82F6]",
  ongoing: "border border-[#3B82F6] bg-[#3B82F633] text-[#3B82F6]",
  completed: "border border-[#22C55E] bg-[#22C55E33] text-[#22C55E]",
  cancelled: "border border-[#DC2626] bg-[#DC262633] text-[#DC2626]",
};

const formatStatusLabel = (status) => {
  if (typeof status !== "string" || !status) {
    return "Scheduled";
  }

  return status
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const formatClockTime = (time24) => {
  if (typeof time24 !== "string" || !time24.includes(":")) {
    return "";
  }

  const [hourStr, minuteStr] = time24.split(":");
  const hour24 = parseInt(hourStr, 10);

  if (Number.isNaN(hour24)) {
    return "";
  }

  const period = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;

  return `${hour12}:${minuteStr || "00"} ${period}`;
};

const formatSessionDate = (dateString) => {
  if (!dateString) {
    return "";
  }

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const mapLiveSessionToCard = (item) => {
  const enrollment = item?.enrollment || {};
  const course = enrollment?.course || {};
  const student = enrollment?.user || {};

  const statusKey =
    typeof item?.sessionStatus === "string"
      ? item.sessionStatus.toLowerCase()
      : "scheduled";

  return {
    id: item?._id,
    courseName: course?.courseTitle || "Untitled Course",
    level: enrollment?.enrollmentType || "",
    status: statusKey,
    studentName:
      `${student?.firstName || ""} ${student?.lastName || ""}`.trim() ||
      "N/A",
    module: item?.sessionLink ? `Link: ${item.sessionLink}` : "",
    sessionDate: formatSessionDate(item?.sessionDate),
    sessionTime:
      item?.startTime && item?.endTime
        ? `${formatClockTime(item.startTime)} - ${formatClockTime(item.endTime)}`
        : "",
    raw: item,
  };
};

const SessionMeta = ({ icon: Icon, iconClassName, value }) => (
  <div className="flex items-center gap-[3px] text-sm  leading-4 text-[#6B7280]">
    <Icon className={iconClassName} />
    <span>{value}</span>
  </div>
);

const InfoIcon = () => (
  <svg width="32" height="32" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
      fill="#9CA3AF"
    />
  </svg>
);

const LiveSessionCard = ({ session }) => {
  const statusClass =
    STATUS_STYLE_MAP[session.status] || STATUS_STYLE_MAP.scheduled;

  return (
    <article className="rounded-[7px] border border-[#D9D9D9A1] bg-white px-3 pb-[7px] pt-3 sm:pl-[22px] sm:pr-3">
      <div className="flex items-start justify-between gap-2 sm:gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
          <h4 className="text-lg font-medium leading-[26px] text-black sm:text-[22px]">
            {session.courseName}
          </h4>
          {session.level ? (
            <span className="rounded-full border border-[#923D8C] bg-[#923D8C33] px-3 py-[3px] text-xs capitalize leading-[14px] text-[#923D8C] sm:px-4">
              {session.level}
            </span>
          ) : null}
        </div>
        <span
          className={`shrink-0 rounded-full px-3 py-[3px] text-xs capitalize leading-[14px] sm:px-[14.5px] ${statusClass}`}
        >
          {formatStatusLabel(session.status)}
        </span>
      </div>

      <div className="mt-[5px] text-base leading-[22px] text-[#595959] sm:text-[19px]">
        <p>Student: {session.studentName}</p>
        {session.module ? (
          <p className="mt-[1px] truncate">{session.module}</p>
        ) : null}
      </div>

      <div className="mt-1.5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-4 sm:gap-8">
          <SessionMeta icon={Calendar} iconClassName="w-[18px] h-[18px]" value={session.sessionDate || "Not scheduled"} />
          <SessionMeta icon={Clock} iconClassName="w-[15px] h-[15px]" value={session.sessionTime || "-"} />
        </div>
      </div>
    </article>
  );
};

const InstructorLiveSessions = ({ sessions = [] }) => {
  const mappedSessions = useMemo(
    () =>
      Array.isArray(sessions) ? sessions.map(mapLiveSessionToCard) : [],
    [sessions]
  );

  const hasSessions = mappedSessions.length > 0;

  return (
    <section className="rounded-xl border border-[#60555557] bg-white px-4 pb-[18px] pt-[19px] sm:pl-[30px] sm:pr-[18px]">
      <h3 className="text-xl font-medium leading-[23px] text-black">
        Live Sessions
      </h3>

      {hasSessions ? (
        <div className="mt-[18px] space-y-[7px]">
          {mappedSessions.map((session) => (
            <LiveSessionCard key={session.id} session={session} />
          ))}
        </div>
      ) : (
        <div className="mt-4 flex flex-col items-center justify-center gap-2 rounded-lg bg-[#F9FAFB] px-4 py-8 text-center">
          <InfoIcon />
          <p className="text-base leading-[19px] text-[#6B7280]">
            No live sessions scheduled yet.
          </p>
        </div>
      )}
    </section>
  );
};

export default InstructorLiveSessions;
